import * as React from 'react';

import type {SalesDocumentUpdateInput} from 'nutshell-graphql-types';

import type {SalesDocumentType} from '../../lead-sidebar/sales-documents/types';
import {SaveableWrapper} from '../../saveable-wrapper/saveable-wrapper';

import {QuoteHeaderEditable} from '../editable/quote-header-editable';
import {HEADER} from '../helpers';
import {QuoteHeader} from '../read-only/quote-header';

type Props = {
    type: SalesDocumentType;
    onSave: (arg1: SalesDocumentUpdateInput) => void;
    onCancel: () => void;
    isActive: boolean;
    id: string;
    headerLeftColumn: string;
    headerRightColumn: string;
    logo: string | null | undefined;
    onToggleActive: (arg1: string) => void;
    forceSave: boolean;
    isTemplate: boolean;
    isImmutable: boolean;
};

export function QuoteHeaderWrapper(props: Props) {
    const {id, onSave, logo: initialLogo} = props;

    const [headerLeftColumn, setLeftHeaderText] = React.useState<string>(props.headerLeftColumn);
    const [headerRightColumn, setRightHeaderText] = React.useState<string>(props.headerRightColumn);
    const [logo, setLogo] = React.useState<string | null>(initialLogo || null);

    React.useEffect(() => {
        setLeftHeaderText(props.headerLeftColumn);
        setRightHeaderText(props.headerRightColumn);
        setLogo(initialLogo || null);
    }, [props.headerLeftColumn, props.headerRightColumn, initialLogo]);

    const handleSave = React.useCallback(() => {
        const newHeader: SalesDocumentUpdateInput = {
            salesDocument: id,
            headerLeftColumn,
            headerRightColumn,
        };

        // Only pass the logo if it has changed
        if (logo !== initialLogo) {
            newHeader.logo = logo;
        }

        onSave(newHeader);
    }, [id, onSave, headerLeftColumn, headerRightColumn, logo, initialLogo]);

    React.useEffect(() => {
        if (props.forceSave) {
            handleSave();
        }
    }, [props.forceSave, handleSave]);

    const handleCancel = () => {
        setLeftHeaderText(props.headerLeftColumn);
        setRightHeaderText(props.headerRightColumn);
        setLogo(props.logo || null);
        props.onCancel();
    };

    return (
        <SaveableWrapper
            isActive={props.isActive}
            onSave={handleSave}
            onCancel={handleCancel}
            onToggleActive={props.onToggleActive}
            sectionName={HEADER}
            noPadding={true}
            isImmutable={props.isImmutable}
            showEditIcon={props.isTemplate}
        >
            {props.isActive ? (
                <QuoteHeaderEditable
                    type={props.type}
                    leftSection={headerLeftColumn}
                    setLeftSection={setLeftHeaderText}
                    rightSection={headerRightColumn}
                    setRightSection={setRightHeaderText}
                    logo={logo}
                    setLogo={setLogo}
                />
            ) : (
                <QuoteHeader
                    type={props.type}
                    leftHeaderText={headerLeftColumn}
                    rightHeaderText={headerRightColumn}
                    logo={logo}
                />
            )}
        </SaveableWrapper>
    );
}
